import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLazyViewUserPlantInvertersQuery } from "../../../../redux/api/generation/generationAPI";
import GroWattDeviceLogItem from "../../../../shared/components/gro-watt-device-log-item/gro-watt-device-log-item";
import SiteDeviceLogItem from "../../../../shared/components/site-device-log-item/site-device-log-item";
import IViewUserSolarPlantInvertersResponseDTO from "../../../../shared/oversight-core/dtos/response-dtos/view-user-solar-plantInverters-response-dto";
import { ESolarServiceProvider } from "../../../../shared/oversight-core/enums/solar-service-provider";
import { IBatteryInverterView } from "../../../../shared/oversight-core/interfaces/battery-inverter-view";
import { IExtendGenericSolarPlantInverters } from "../../../../shared/oversight-core/interfaces/generic-page-solar-plant-inverter-view";
import { IGridInverterView } from "../../../../shared/oversight-core/interfaces/grid-inverter-view";
import { IGroWattInverterView } from "../../../../shared/oversight-core/interfaces/gro-watt-inverter-view";
import { IPowerHubDeviceView } from "../../../../shared/oversight-core/interfaces/powerhub-device-view";
import InverterInfoCard from "../../../../shared/oversight-core/shared-components/inverter-info-card/inverter-info-card";
import AppButton from "../../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import MaterialIcon from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import Pagination from "../../../../shared/oversight-core/ui-elements/pagination/pagination";
import SpinnerModal from "../../../../shared/oversight-core/ui-elements/spinner/spinner";
import {
  getOpenInButtonText,
  openServiceProviderPortal,
} from "../../../../shared/oversight-core/utils/service-provider-portal-utils";

interface IProps {
  plantId: string;
  serviceProvider: ESolarServiceProvider;
}

const SiteDevices = (props: IProps) => {
  const { plantId, serviceProvider } = props;

  const [goodWeGridInverters, setGoodWeGridInverters] = useState<
    IGridInverterView[]
  >([]);
  const [goodWeBatteryInverters, setGoodWeBatteryInverters] = useState<
    IBatteryInverterView[]
  >([]);
  const [vrmInverters, setVrmInverters] = useState<
    IExtendGenericSolarPlantInverters[]
  >([]);
  const [groWattInverters, setGroWattInverters] = useState<
    IGroWattInverterView[]
  >([]);
  const [powerHubDevices, setPowerHubDevices] = useState<IPowerHubDeviceView[]>(
    [],
  );
  const [totalElements, setTotalElements] = useState(0);
  const [filter, setFilter] = useState({ pageNumber: 0 });

  const [triggerUserPlantInverters, { isFetching }] =
    useLazyViewUserPlantInvertersQuery();

  useEffect(() => {
    if (plantId) {
      triggerUserPlantInverters({
        plantId: plantId,
        pageNumber: filter.pageNumber,
        pageSize: 5,
      })
        .unwrap()
        .then((res: IViewUserSolarPlantInvertersResponseDTO) => {
          setGoodWeGridInverters(res.genericPage.elements[0].gridInverterViews);
          setGoodWeBatteryInverters(
            res.genericPage.elements[0].batteryInverterViews,
          );
          setVrmInverters(res.genericPage.elements);
          setGroWattInverters(res.genericPage.elements);
          setPowerHubDevices(res.genericPage.elements);
          setTotalElements(res.genericPage.totalElements);
        })
        .catch(() => {
          setTotalElements(0);
        });
    }
  }, [triggerUserPlantInverters, plantId, filter]);

  const renderGoodWeInverters = () => {
    if (goodWeGridInverters && goodWeGridInverters.length > 0) {
      return goodWeGridInverters.map((goodWeGridInverter, index) => (
        <InverterInfoCard key={index} gridInverter={goodWeGridInverter} />
      ));
    }

    if (goodWeBatteryInverters && goodWeBatteryInverters.length > 0) {
      return goodWeBatteryInverters.map((goodWeBatteryInverter, index) => (
        <InverterInfoCard key={index} batteryInverter={goodWeBatteryInverter} />
      ));
    }

    if (!isFetching) {
      return (
        <div className="mt-4 container-dash text-center text-light font-weight-400 font-size-14">
          There are no inverters
        </div>
      );
    }

    return null;
  };

  return (
    <div className="position-relative">
      <Row>
        <Col className="col-auto">
          <AppButton
            iconName="open_in_new"
            iconWeight={700}
            text={getOpenInButtonText(serviceProvider)}
            variant="transparentWithBorder"
            className="font-size-14"
            onClick={() => openServiceProviderPortal(serviceProvider)}
          />
        </Col>
      </Row>
      {serviceProvider === ESolarServiceProvider.GOODWE && (
        <Row className="flex-column gy-2 mt-4 mx-0">
          {renderGoodWeInverters()}
        </Row>
      )}
      {serviceProvider === ESolarServiceProvider.VRM && (
        <Row className="flex-column gy-2 mt-4">
          {vrmInverters && vrmInverters.length > 0 ? (
            vrmInverters.map((vrmInverter, index) => {
              return (
                <Col key={index}>
                  <SiteDeviceLogItem inverter={vrmInverter} />
                </Col>
              );
            })
          ) : (
            <>
              {!isFetching && (
                <div className="mt-4 container-dash text-center text-light font-weight-400 font-size-14">
                  There are no inverters
                </div>
              )}
            </>
          )}
        </Row>
      )}
      {serviceProvider === ESolarServiceProvider.GROWATT && (
        <Row className="flex-column gy-2 mt-4">
          {groWattInverters && groWattInverters.length > 0 ? (
            groWattInverters.map((growattInverter, index) => {
              return (
                <Col key={index}>
                  <GroWattDeviceLogItem inverter={growattInverter} />
                </Col>
              );
            })
          ) : (
            <>
              {!isFetching && (
                <div className="mt-4 container-dash text-center text-light font-weight-400 font-size-14">
                  There are no inverters
                </div>
              )}
            </>
          )}
        </Row>
      )}
      {serviceProvider === ESolarServiceProvider.POWERHUB && (
        <Row className="flex-column gy-2 mt-4">
          {powerHubDevices && powerHubDevices.length > 0 ? (
            powerHubDevices.map((device, index) => {
              return (
                <Col key={index}>
                  <div className="container-white p-3">
                    <Row className="align-items-center">
                      <Col className="col-auto">
                        <MaterialIcon icon="devices" size={20} />
                      </Col>
                      <Col>
                        <div className="text-dark font-weight-500 font-size-14">
                          {device.din || "Unknown Device"}
                        </div>
                        <div className="text-light font-weight-400 font-size-12">
                          Model: {device.model || "N/A"}
                        </div>
                        <div className="text-light font-weight-400 font-size-12">
                          Type: {device.deviceType || "N/A"}
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
              );
            })
          ) : (
            <>
              {!isFetching && (
                <div className="mt-4 container-dash text-center text-light font-weight-400 font-size-14">
                  There are no devices available
                </div>
              )}
            </>
          )}
        </Row>
      )}
      <Row>
        <Col>
          <Pagination
            itemsPerPage={5}
            length={totalElements}
            currentPage={filter.pageNumber + 1}
            updateCurrentPage={(pn) => {
              setFilter((ps) => ({ ...ps, pageNumber: pn - 1 }));
            }}
          />
        </Col>
      </Row>
      <SpinnerModal show={isFetching} />
    </div>
  );
};

export default SiteDevices;
